<!-- LAYOUT: app.vue -->
<!-- Main authenticated layout — navigation drawer, app bar, page slot, and global snackbar. -->

<script setup lang="ts">
//
// Imports
//

// Vuetify
import { useDisplay } from "vuetify/lib/composables/display.mjs";

// Components
import AppBar from "../components/common/app-bar/template/AppBar.vue";
import Drawer from "../components/common/navigation-drawer/template/Drawer.vue";
import GlobalSnackbar from "../components/shared/GlobalSnackbar.vue";

//
// Composables
//

// Breakpoints
const { smAndDown } = useDisplay();

// Localization
const { t, locale } = useI18n();

// Auth
const { userName, userEmail } = useAuth();

// Page Title
const { pageTitle } = useAppBar();

// Runtime Config
const config = useRuntimeConfig();

//
// Watchers
//

// Update document head when page title or locale changes
watch(
  [pageTitle, locale],
  () => {
    useHead({
      title: pageTitle.value + " - " + t("components.common.navigation-drawer.drawer_header.app_name"),

      htmlAttrs: {
        lang: locale.value,
      },

      link: [
        {
          rel: "icon",
          type: "image/x-icon",
          href: pathCombine(config.public.APP_BASE_URL, "/favicon.ico"),
        },
      ],

      meta: [
        {
          name: "author",
          content: "SKYWARE Group",
        },
        { name: "robots", content: "index, follow" },
        { name: "viewport", content: "width=device-width, initial-scale=1" },

        // Open Graph
        { property: "og:title", content: pageTitle.value },
        { property: "og:type", content: "website" },
      ],
    });
  },
  { immediate: true, deep: true },
);
</script>

<template>
  <div>
    <!-- Navigation Drawer -->
    <Drawer
      :app-name="t('components.common.navigation-drawer.drawer_header.app_name')"
      :user-name="userName"
      :user-email="userEmail"
    />

    <!-- App Bar -->
    <AppBar :show-page-title="!smAndDown" />

    <v-main>
      <v-container fluid class="pa-0">
        <slot />
      </v-container>
    </v-main>

    <!-- Snackbar -->
    <GlobalSnackbar />
  </div>
</template>
